/**
 * The outcome prompt (docs/11 §3.7) — two taps is the entire ceiling on what
 * a working driver will give, and this is the only signal that improves
 * tomorrow's predictions. Always skippable, never blocking.
 */
import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { api } from './api.js';
import type { StoredAttempt } from './storage.js';
import { Sheet, Button } from './ui.js';

const WAIT_BANDS: [string, string][] = [['<5', 'Under 5'], ['5-15', '5–15'], ['15-30', '15–30'], ['30+', '30+']];

export function OutcomeSheet({ attempt, onDone }: { attempt: StoredAttempt; onDone: () => void }) {
  const [gotRide, setGotRide] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(ride: boolean, wait?: string) {
    setBusy(true);
    try { await api.outcome(attempt.id, ride, wait); } catch { /* dup or closed: fine */ }
    onDone();
  }

  return (
    <Sheet title={`How did ${attempt.title} go?`} onClose={onDone} labelledBy="outcome-title">
      {gotRide === null ? (
        <div className="btn-row">
          <Button variant="primary" busy={busy} icon={<ThumbsUp size={22} aria-hidden="true" />}
            onClick={() => setGotRide(true)}>Got a ride</Button>
          <Button variant="secondary" busy={busy} icon={<ThumbsDown size={22} aria-hidden="true" />}
            onClick={() => void submit(false)}>Nothing</Button>
        </div>
      ) : (
        <>
          <p className="sub">Wait before the first fare</p>
          <div className="chips">
            {WAIT_BANDS.map(([v, label]) => (
              <button key={v} className="chip grow" disabled={busy} onClick={() => void submit(true, v)}>
                {label} min
              </button>
            ))}
          </div>
        </>
      )}
      <Button variant="quiet" onClick={onDone} disabled={busy}>Skip</Button>
    </Sheet>
  );
}
